import BaseComponent from '@/components/base';
import { style } from './style';
import { FontStyle, ButtonStyles } from '@/components/style';
import CustomSelect from '@/components/custom-select';
import { fireGTMEvent, accountGTMEventsMap } from '@/utils';

export default class ShopAllFilters extends BaseComponent {
  onConstruct() {
    customElements.define('custom-select', CustomSelect);
    this.textProductLine =
      this.shopAllFilters.filters.find(
        (filter) => filter.value === 'product_line'
      ).text || 'For';
    this.textProductType =
      this.shopAllFilters.filters.find(
        (filter) => filter.value === 'product_type'
      ).text || "I'm looking for";
    this.textProductConcern =
      this.shopAllFilters.filters.find(
        (filter) => filter.value === 'product_concern'
      ).text || 'that suits';
  }
  render() {
    return `
    ${style}
    ${FontStyle}
    ${ButtonStyles}
    <div class="filter-products" data-filter-container>
        <div class="filter-wrapper">
        <div class="filters-container">${
          this.textProductLine
        } ${this.selectOption('product_line')} ${
          this.textProductType
        } ${this.selectOption('product_type')} ${
          this.textProductConcern
        } ${this.selectOption('product_concern')} </div>
            <div class="filter-field">
                <button type="submit" class="cta cta-2 filter-button" id="filter-button" style="border-color: #000000">
                    <span class="text-button">DISCOVER <span class="dash"></span> YOUR MATCH</span>
                </button>
                <button class="cta cta-link-1 reset-button" id="reset-button"> Reset </button>
            </div>
        </div>
    <span id="no-results" class="no-results hide">No results found</span>
    </div>
    `;
  }

  get classNameToFilter() {
    return `.${this.getAttribute('class-name-to-filter')}` || '';
  }

  afterMount() {
    const filterButton = this.shadowRoot.querySelector('#filter-button');
    const resetButton = this.shadowRoot.querySelector('#reset-button');

    const selectedValues = this.getFiltersFromQueryParams();

    if (selectedValues.length > 0) {
      this.setInitialSelectValues(selectedValues);
      this.applyFilters(selectedValues);
    }

    // Add event listeners to all select elements
    filterButton.addEventListener('click', (e) => {
      const allSelects = this.shadowRoot.querySelectorAll('custom-select');
      const allSelectsElements = [];
      allSelects.forEach((select) => {
        allSelectsElements.push(select.shadowRoot.querySelector('select'));
      });
      const selectedValues = [];
      const productsFound = [];
      const gtmEventData = {};
      allSelectsElements.forEach((select) => {
        const selectedValue = select.value;
        const optionValue = select.name;
        if (selectedValue) {
          selectedValues.push({ optionValue, selectedValue });
          gtmEventData[optionValue] = selectedValue;
        }
      });

      this.applyFilters(selectedValues);
      this.updateQueryParamsWithFilters(selectedValues);
    });

    resetButton.addEventListener('click', (e) => {
      this.clearFiltersFromQueryParams();
      this.resetCustomSelects();
      const allProducts = document.querySelectorAll(this.classNameToFilter);
      allProducts.forEach((product) => {
        product.style.display = 'block';
      });
      const noResults = this.shadowRoot.querySelector('#no-results');
      noResults.classList.add('hide');
      fireGTMEvent(accountGTMEventsMap['shop_all_filters_reset']);
    });
  }

  /**
   * Get shop all filters from URL query parameters
   * @returns {Array} - Array of selected filters
   */
  getFiltersFromQueryParams() {
    const params = new URLSearchParams(window.location.search);
    const filters = [];

    for (const [key, value] of params.entries()) {
      if (key.startsWith('filter_')) {
        filters.push({
          optionValue: key.replace('filter_', ''),
          selectedValue: value
        });
      }
    }

    return filters;
  }

  /**
   * Remove shop all filters from URL query parameters
   * @returns {URLSearchParams} - URLSearchParams object with filter parameters removed
   */
  removeFiltersFromQueryParams() {
    const params = new URLSearchParams(window.location.search);

    // Clear existing filter parameters only
    const keysToDelete = [];
    for (const key of params.keys()) {
      if (key.startsWith('filter_')) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach((key) => params.delete(key));

    return params;
  }

  /**
   * Update URL query parameters with the given shop all filters and refresh the URL
   * @param {Array} selectedValues - Array of selected filter objects with optionValue and selectedValue
   * @returns {void}
   */
  updateQueryParamsWithFilters(selectedValues) {
    const newParams = this.removeFiltersFromQueryParams();
    selectedValues.forEach(({ optionValue, selectedValue }) => {
      newParams.set(`filter_${optionValue}`, selectedValue);
    });

    this.refreshUrl(newParams);
  }

  /**
   * Clear shop all filter parameters from URL query string and refresh the URL
   * @returns {void}
   */
  clearFiltersFromQueryParams() {
    const params = this.removeFiltersFromQueryParams();
    this.refreshUrl(params);
  }

  /**
   * Reset all custom select components to their default state
   * @returns {void}
   */
  resetCustomSelects() {
    const allSelects = this.shadowRoot.querySelectorAll('custom-select');
    allSelects.forEach((select) => {
      select.resetToDefault();
    });
  }

  /**
   * Update the browser URL with new query parameters without reloading the page
   * @param {URLSearchParams} params - The URLSearchParams object containing the new query parameters
   * @returns {void}
   */
  refreshUrl(params) {
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }

  /**
   * Set initial values for custom select components based on URL filter parameters
   * @param {Array} selectedValues - Array of filter objects from URL
   * @returns {void}
   */
  setInitialSelectValues(selectedValues) {
    selectedValues.forEach((filter) => {
      const customSelect = this.shadowRoot.querySelector(
        `custom-select[name="${filter.optionValue}"]`
      );
      if (!customSelect) {
        return;
      }
      customSelect.setValue(filter.selectedValue);
    });
  }

  applyFilters(selectedValues) {
    let productsFound = [];
    let gtmEventData = {};

    if (selectedValues.length > 0) {
      fireGTMEvent(accountGTMEventsMap['shop_all_filters_used'], gtmEventData);
      selectedValues.forEach((selectedValue) => {
        const optionValueFormatted = selectedValue.optionValue.replace(
          '_',
          '-'
        );
        const products = document.querySelectorAll(
          `[data-${optionValueFormatted}*="${selectedValue.selectedValue}"]`
        );
        productsFound.push({
          optionValue: optionValueFormatted,
          selectedValue: selectedValue.selectedValue,
          products: products
        });
      });

      // Get all products that match all selected filters
      let commonProducts = [];

      if (productsFound.length > 0) {
        // Start with the first filter's products
        commonProducts = Array.from(productsFound[0].products);

        // Intersect with each subsequent filter
        for (let i = 1; i < productsFound.length; i++) {
          const currentFilter = productsFound[i];
          const currentProducts = Array.from(currentFilter.products);

          // Keep only products that exist in both arrays
          commonProducts = commonProducts.filter((product) => {
            const found = currentProducts.some(
              (currentProduct) => currentProduct === product
            );
            return found;
          });
        }
      }

      // If any filter has no products, clear all results
      productsFound.forEach((product) => {
        if (product.products.length === 0) {
          commonProducts.length = 0;
        }
      });

      const allProducts = document.querySelectorAll(this.classNameToFilter);
      allProducts.forEach((product) => {
        if (commonProducts.includes(product)) {
          product.style.display = 'block';
        } else {
          product.style.display = 'none';
        }
      });
      if (commonProducts.length === 0) {
        const noResults = this.shadowRoot.querySelector('#no-results');
        noResults.classList.remove('hide');
      } else {
        const noResults = this.shadowRoot.querySelector('#no-results');
        noResults.classList.add('hide');
      }
    } else {
      const allProducts = document.querySelectorAll(this.classNameToFilter);
      allProducts.forEach((product) => {
        product.style.display = 'block';
      });
      const noResults = this.shadowRoot.querySelector('#no-results');
      noResults.classList.add('hide');
    }
  }

  get shopAllFilters() {
    const filtersJson = this.data.shopAllFilters.value[0].filters_json ?? {
      filters_json: []
    };
    return filtersJson;
  }

  selectOption(filterType) {
    const filter = this.filterOption(filterType);
    if (!filter) {
      return '';
    }
    const selectOptionHtml = `
     <div class="filter-field">
      <custom-select name="${filter.value}">
        ${filter.options.map((option) => {
          return `<option value="${option.value}">${option.label}</option>`;
        })}
      </custom-select>
    </div>
    `;
    return selectOptionHtml;
  }

  filterOption(filterType) {
    const filtersJson = this.data.shopAllFilters.value[0].filters_json ?? {
      filters_json: []
    };
    const filter = filtersJson.filters.find((filter) => {
      return filter.value === filterType;
    });
    return filter;
  }
}
